/**
 * T025/US5 (FR-015). Columns for `Exceptions.tsx`, on `List.tsx`'s own fan-out precedent (a plain
 * `ColumnDef[]` over an already-fetched, unpaged array — `GET /stock/exceptions` has no pagination
 * and no `kind` filter, verified against `InventoryController.GetStockExceptions`).
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { AvatarCard, StockFigure } from "@steelyard/theme";
import { formatMoment } from "../../lib/format";
import { formatQty, type StockExceptionRow } from "../../lib/services/warehouse";
import { PiArrowsDownUpDuotone as MovementIcon, PiEyeDuotone as EyeIcon } from "react-icons/pi";
import { RowActions } from "../../components/table/row-actions";
import { inventoryPath } from "../../lib/paths";

export type ExceptionRow = StockExceptionRow & { code: string; name: string; unitCode: string };

/** A quantity column's sort key: the API's own figure read as a number. Shown through `StockFigure` as sent. */
const quantity = (value: number | string | null | undefined) => {
  const parsed = Number(value);
  return value === null || value === undefined || !Number.isFinite(parsed) ? undefined : parsed;
};

// Accessors so the toolbar's search matches a SKU's name and code, and every header sorts — the
// whole exception set is loaded, so both cover every row.
export const exceptionsColumns: ColumnDef<ExceptionRow>[] = [
  {
    id: "actions",
    header: "",
    // The row's ⋮ menu, as on every list: the SKU's stock position, and its movement report.
    cell: ({ row }) => (
      <RowActions
        name={row.original.code}
        primary={{ label: "View stock position", icon: EyeIcon, to: inventoryPath(row.original.skuId) }}
        more={[{ label: "Stock movement report", icon: MovementIcon, to: `/reports/stock-movement?skuId=${row.original.skuId}` }]}
      />
    ),
  },
  {
    id: "sku",
    accessorFn: (row) => `${row.name} ${row.code}`,
    header: "SKU",
    size: 260,
    cell: ({ row }) => (
      <Link to={inventoryPath(row.original.skuId)} className="hover:underline">
        <AvatarCard src="" name={row.original.name} description={<span className="font-mono">{row.original.code}</span>} />
      </Link>
    ),
  },
  {
    id: "onHand",
    accessorFn: (row) => quantity(row.qtyStockingUnit),
    sortUndefined: "last",
    meta: { align: "right" },
    header: "On hand",
    size: 150,
    // FR-012: rendered exactly as returned — negative, never clamped to zero.
    cell: ({ row }) => <StockFigure compact label="On hand" value={formatQty(row.original.qtyStockingUnit)} unit={row.original.unitCode} />,
  },
  {
    id: "free",
    accessorFn: (row) => quantity(row.freeStock),
    sortUndefined: "last",
    meta: { align: "right" },
    header: "Free",
    size: 150,
    cell: ({ row }) => <StockFigure compact label="Free" value={formatQty(row.original.freeStock)} unit={row.original.unitCode} />,
  },
  {
    id: "since",
    // ISO timestamp — sorts chronologically as text.
    accessorKey: "negativeSince",
    header: "Negative since",
    size: 180,
    cell: ({ row }) => (row.original.negativeSince ? formatMoment(row.original.negativeSince) : "—"),
  },
  {
    id: "beyondPeriod",
    accessorKey: "negativeBeyondPeriod",
    header: "Beyond reporting period",
    size: 170,
    cell: ({ row }) =>
      row.original.negativeBeyondPeriod ? (
        <span className="text-red-dark font-medium">Yes — needs attention</span>
      ) : (
        <span className="text-muted-foreground">No</span>
      ),
  },
];
